import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { firstAvailableCabinetPath } from '../utils/cabinet-sections';

/**
 * BE-807: гвард раздела кабинета по ключу права. Меню прячет пункт, но прямая ссылка
 * должна упираться в тот же запрет — иначе раздел открывается с пачкой 403.
 *
 * Отличий от `permissionGuard` два. Право спрашивается в паре с `business_id` из
 * родительского роута: ключи не перетекают между юрлицами, и сотрудник двух бизнесов
 * иначе получил бы в обоих то, что выдано в одном. И уводит он внутрь того же кабинета,
 * а не в список бизнесов: список сотруднику доступен, но показывать там единственный
 * бизнес, из которого его только что выбросило, — тупик.
 *
 * Ключей может быть несколько — тогда раздел открывает любой из них: «История сканов»
 * доступна и по заказам, и по истории сканирований. Набор обязан совпадать с картой
 * разделов (`CABINET_SECTIONS`), иначе меню и гвард разойдутся.
 */
export function cabinetPermissionGuard(key: string | readonly string[]): CanActivateFn {
  const keys = Array.isArray(key) ? key : [key as string];
  return (route, state) => {
    const auth = inject(AuthService);
    const router = inject(Router);
    const businessId = route.parent?.paramMap.get('business_id') ?? null;

    // Без контекста бизнеса судить не о чем: роут кабинета всегда лежит под :business_id,
    // а отсутствие параметра означает, что гвард поставили не туда.
    if (!businessId || keys.some((item) => auth.canInCabinet(item, businessId))) {
      return true;
    }

    const fallback = firstAvailableCabinetPath(auth.authState(), businessId);
    // Сравниваем путь роута целиком, а не префикс URL и не первый его сегмент:
    // `profile/edit` и `profile` — разные роуты с разными правами, и любое сравнение
    // «по началу» пустило бы сотрудника с одним лишь «Просмотром профиля» в форму
    // редактирования. Форма открылась бы, а 403 он получил бы уже на сохранении,
    // потеряв заполненное.
    //
    // Уводить некуда: закрытый роут и есть первый доступный раздел. Редирект в самого
    // себя закольцевал бы гварды, поэтому пускаем и оставляем отказ бэкенду.
    if (route.routeConfig?.path === fallback) {
      return true;
    }
    return router.createUrlTree(['/businesses', businessId, fallback]);
  };
}
